import { AlertTriangle, X } from 'lucide-react';

export interface ConfirmModalProps {
  /** Contrôle l'affichage du modal */
  isOpen: boolean;
  /** Titre de la demande de confirmation */
  title: string;
  /** Message explicatif (conséquences de l'action) */
  message: string;
  /** Label du bouton de confirmation (défaut : "Confirmer") */
  confirmLabel?: string;
  /** Label du bouton d'annulation (défaut : "Annuler") */
  cancelLabel?: string;
  /** Style du bouton de confirmation : 'danger' (rouge) ou 'primary' (bleu) */
  variant?: 'danger' | 'primary';
  /** Affiche un spinner et désactive les boutons pendant l'opération */
  isLoading?: boolean;
  /** Appelé quand l'utilisateur confirme */
  onConfirm: () => void;
  /** Appelé quand l'utilisateur annule (ou clique sur le backdrop) */
  onCancel: () => void;
}

/**
 * Modal de confirmation réutilisable.
 *
 * Usage :
 * ```tsx
 * <ConfirmModal
 *   isOpen={!!itemToDelete}
 *   title="Supprimer l'élément"
 *   message={`Voulez-vous vraiment supprimer "${itemToDelete?.name}" ? Cette action est irréversible.`}
 *   onConfirm={() => deleteMutation.mutate(itemToDelete!.id)}
 *   onCancel={() => setItemToDelete(null)}
 *   isLoading={deleteMutation.isPending}
 * />
 * ```
 */
export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'danger',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const confirmClass =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onCancel} />

      {/* Panel */}
      <div className='relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6'>
        {/* Header */}
        <div className='flex items-start gap-4 mb-4'>
          {variant === 'danger' && (
            <div className='w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5'>
              <AlertTriangle className='w-5 h-5 text-red-600' />
            </div>
          )}
          <div className='flex-1 min-w-0'>
            <h2 className='text-base font-bold text-slate-900'>{title}</h2>
            <p className='text-sm text-slate-500 mt-1 leading-relaxed'>{message}</p>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className='p-1 rounded-lg hover:bg-slate-100 transition-colors shrink-0 disabled:opacity-50  cursor-pointer'
          >
            <X className='w-4 h-4 text-slate-400' />
          </button>
        </div>

        {/* Actions */}
        <div className='flex gap-3 justify-end mt-6'>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className='px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50 cursor-pointer'
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-xl transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmClass} cursor-pointer`}
          >
            {isLoading ? 'En cours…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
